import { supabase } from './supabase';

export interface KPIDashboard {
  id: string;
  business_id: string;
  title: string;
  default_date_range?: string;
  created_device_type: 'desktop' | 'mobile';
  desktop_configured: boolean;
  mobile_configured: boolean;
  created_at: string;
  updated_at: string;
}

export type ReportSize = 'small' | 'medium' | 'large' | 'xl';

export interface KPIDashboardReport {
  id: string;
  dashboard_id: string;
  business_id: string;
  report_type: string;
  report_title: string;
  layout_x: number;
  layout_y: number;
  layout_w: number;
  layout_h: number;
  view_type: 'desktop' | 'mobile';
  size?: ReportSize; // New size field
  report_data: any;
  created_at: string;
  updated_at: string;
}

export async function getKPIDashboards(businessId: string): Promise<KPIDashboard[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('kpi_dashboards')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as KPIDashboard[];
}

// Utility function to detect device type
export function detectDeviceType(): 'desktop' | 'mobile' {
  return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

export async function createKPIDashboard(
  dashboard: Omit<KPIDashboard, 'id' | 'created_at' | 'updated_at' | 'created_device_type' | 'desktop_configured' | 'mobile_configured'>,
  deviceType?: 'desktop' | 'mobile'
): Promise<KPIDashboard> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const detectedDeviceType = deviceType || detectDeviceType();
  
  // IMPORTANT: Only configure for the current view type to ensure independence
  const dashboardData = {
    ...dashboard,
    created_device_type: detectedDeviceType,
    desktop_configured: detectedDeviceType === 'desktop',
    mobile_configured: detectedDeviceType === 'mobile'
  };
  
  const { data, error } = await supabase
    .from('kpi_dashboards')
    .insert([dashboardData])
    .select()
    .single();

  if (error) throw error;
  return data as KPIDashboard;
}

export async function updateKPIDashboard(id: string, updates: Partial<Pick<KPIDashboard, 'title' | 'default_date_range'>>): Promise<KPIDashboard> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('kpi_dashboards')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as KPIDashboard;
}

export async function deleteKPIDashboard(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { error } = await supabase
    .from('kpi_dashboards')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Dashboard Reports Functions
export async function getDashboardReports(dashboardId: string, viewType?: 'desktop' | 'mobile'): Promise<KPIDashboardReport[]> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  let query = supabase
    .from('kpi_dashboard_reports')
    .select('*')
    .eq('dashboard_id', dashboardId);
    
  if (viewType) {
    query = query.eq('view_type', viewType);
  }
  
  const { data, error } = await query.order('created_at', { ascending: true });

  if (error) throw error;
  return data as KPIDashboardReport[];
}

export async function createDashboardReport(report: Omit<KPIDashboardReport, 'id' | 'created_at' | 'updated_at'>): Promise<KPIDashboardReport> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('kpi_dashboard_reports')
    .insert([report])
    .select()
    .single();

  if (error) throw error;
  return data as KPIDashboardReport;
}

export async function updateDashboardReportLayout(id: string, layout: { layout_x: number; layout_y: number; layout_w: number; layout_h: number; size?: ReportSize }): Promise<KPIDashboardReport> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('kpi_dashboard_reports')
    .update(layout)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as KPIDashboardReport;
}

export async function deleteDashboardReport(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { error } = await supabase
    .from('kpi_dashboard_reports')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function updateDashboardDefaultDateRange(id: string, defaultDateRange: string): Promise<KPIDashboard> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const { data, error } = await supabase
    .from('kpi_dashboards')
    .update({ default_date_range: defaultDateRange })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as KPIDashboard;
}

// Auto-configure opposite view for a dashboard
export async function configureOppositeView(
  dashboardId: string, 
  targetViewType: 'desktop' | 'mobile'
): Promise<{ dashboard: KPIDashboard; reports: KPIDashboardReport[] }> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  // Get the opposite view type
  const sourceViewType = targetViewType === 'desktop' ? 'mobile' : 'desktop';
  
  // Get existing reports from source view
  const sourceReports = await getDashboardReports(dashboardId, sourceViewType);
  
  if (sourceReports.length === 0) {
    throw new Error(`No ${sourceViewType} reports found to configure ${targetViewType} view`);
  }
  
  // Auto-adapt layouts for target view using new fixed grid system
  const adaptedReports = sourceReports.map(report => {
    let adaptedLayout;
    
    if (targetViewType === 'mobile') {
      // Desktop → Mobile: Adapt from 100-column to 20-column grid
      const desktopWidthPercent = (report.layout_w / 100) * 100; // Get percentage of desktop width
      const mobileWidth = Math.max(Math.round((desktopWidthPercent / 100) * 20), 1); // Convert to mobile columns
      
      adaptedLayout = {
        x: Math.min(Math.round((report.layout_x / 100) * 20), 20 - mobileWidth), // Scale X position proportionally
        y: report.layout_y, // Keep similar vertical spacing
        w: Math.min(mobileWidth, 20), // Limit width to max 20 columns
        h: Math.max(Math.floor(report.layout_h * 0.7), 1) // Reduce height by 30% for mobile compactness
      };
    } else {
      // Mobile → Desktop: Adapt from 20-column to 100-column grid  
      const mobileWidthPercent = (report.layout_w / 20) * 100; // Get percentage of mobile width
      const desktopWidth = Math.max(Math.round((mobileWidthPercent / 100) * 100), 10); // Convert to desktop columns, minimum 10
      
      adaptedLayout = {
        x: Math.min(Math.round((report.layout_x / 20) * 100), 100 - desktopWidth), // Scale X position proportionally
        y: Math.floor(report.layout_y * 0.6), // Compact vertically for desktop (more horizontal space)
        w: Math.min(desktopWidth, 100), // Scale width proportionally, max 100 columns
        h: Math.max(report.layout_h + 1, 2) // Increase height slightly for desktop, minimum 2
      };
    }
    
    return {
      dashboard_id: dashboardId,
      business_id: report.business_id,
      report_type: report.report_type,
      report_title: report.report_title,
      layout_x: adaptedLayout.x,
      layout_y: adaptedLayout.y,
      layout_w: adaptedLayout.w,
      layout_h: adaptedLayout.h,
      view_type: targetViewType,
      report_data: report.report_data
    };
  });
  
  // Insert adapted reports
  const { data: newReports, error: reportsError } = await supabase
    .from('kpi_dashboard_reports')
    .insert(adaptedReports)
    .select();
    
  if (reportsError) throw reportsError;
  
  // Update dashboard configuration status
  const configUpdate = targetViewType === 'desktop' 
    ? { desktop_configured: true }
    : { mobile_configured: true };
    
  const { data: updatedDashboard, error: dashboardError } = await supabase
    .from('kpi_dashboards')
    .update(configUpdate)
    .eq('id', dashboardId)
    .select()
    .single();
    
  if (dashboardError) throw dashboardError;
  
  return {
    dashboard: updatedDashboard as KPIDashboard,
    reports: newReports as KPIDashboardReport[]
  };
}

// Mark a view as configured
export async function markViewAsConfigured(
  dashboardId: string, 
  viewType: 'desktop' | 'mobile'
): Promise<KPIDashboard> {
  if (!supabase) throw new Error('Supabase client not initialized');
  
  const configUpdate = viewType === 'desktop' 
    ? { desktop_configured: true }
    : { mobile_configured: true };
    
  const { data, error } = await supabase
    .from('kpi_dashboards')
    .update(configUpdate)
    .eq('id', dashboardId)
    .select()
    .single();
    
  if (error) throw error;
  return data as KPIDashboard;
} 